import { ValueOfObject } from 'src/types/generics';
import { HasStatus, Submittable } from 'src/api/Interfaces';
import { MediaObject } from 'src/api/services/MediaClient';
import { DEFAULT_REQUEST_SORTS } from 'src/features/requests/request.constants';

export const defaultSortFilter = new URLSearchParams({
  sorts: JSON.stringify(DEFAULT_REQUEST_SORTS),
}).toString();

const requestPriority = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
} as const;

type RequestPriorityType = ValueOfObject<typeof requestPriority>;

const requestPriorityLabels = {
  [requestPriority.low]: 'Low',
  [requestPriority.medium]: 'Medium',
  [requestPriority.high]: 'High',
  [requestPriority.critical]: 'Critical',
};

const requestComplexity = {
  simple: 'simple',
  moderate: 'moderate',
  complex: 'complex',
} as const;

type RequestComplexityType = ValueOfObject<typeof requestComplexity>;

const requestComplexityLabels = {
  [requestComplexity.simple]: 'Simple',
  [requestComplexity.moderate]: 'Moderate',
  [requestComplexity.complex]: 'Complex',
};

export const requestListType = {
  list: 'list',
  kanban: 'kanban',
} as const;

export type RequestListType = ValueOfObject<typeof requestListType>;

const requestStatus = {
  uploading: 'uploading',
  queued: 'queued',
  currentlyEditing: 'currently_editing',
  delivered: 'delivered',
  complete: 'complete',
} as const;

type RequestStatus = ValueOfObject<typeof requestStatus>;

// Mirrors the values the backend writes in RevisionCommentObserver::created:
// customer → 'customer', editor/team_lead → 'editor', admin → 'admin'.
const lastMessageBy = {
  customer: 'customer',
  editor: 'editor',
  admin: 'admin',
} as const;

type LastMessageBy = ValueOfObject<typeof lastMessageBy>;

interface RequestOutcome {
  id: string;
  updated_at: string;
}

interface RequestsTableResponse extends HasStatus<RequestStatus>, Submittable {
  id: string;
  flow: 'default' | 'multi_outcome' | 'outcome' | 'repurpose' | string;
  priority: RequestPriorityType;
  title: string;
  // Outcome rows ship with the parent project's id + title so the table can
  // render the project label above the outcome title and route project-level
  // actions (open Edit Request) to the parent. Null on default-flow rows.
  parent_id: string | null;
  parent_title: string | null;
  type: string;
  editor_note?: string;
  expected_editing_time?: number; // Minutes
  expected_reediting_time?: number; // Minutes
  reviewed_by_editor: string;
  reviewed_by_customer: string;
  approved_at?: string;
  transcript_attached?: string;
  requested_reedit_at?: string;
  /** Lifetime count of customer-initiated re-edit requests on this row.
   * Ticks up on every Re-edit click; never cleared by Undo or by the editor
   * re-delivering. Drives the loader-trail signal that persists across an
   * Undo (#2442). */
  reedit_count?: number;
  /** See Request.types.tsx's requested_reedit_at doc for why this exists. */
  has_unresolved_reedit?: boolean;
  requested_attention_at?: string;
  attention_text?: string;
  created_at: Date | string;
  updated_at: Date | string;
  resolutions?: OrderResolutionsObject;
  assigned_editor?: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar?: MediaObject;
  };
  company?: {
    id: string;
    name: string;
  };
  brand?: {
    id: string;
    name: string;
  };

  outcomes_count: number;
  complete_outcomes_count: number;
  delivered_outcomes_count: number;
  queued_outcomes_count: number;
  editing_outcomes_count: number;
  revisions_count?: number;
  rated_revisions_count?: number;
  qa_checked?: boolean;
  due_date: string | null;
  /** be-core #2513: whether this request can be converted to a multi-outcome project. */
  can_be_transformed_to_multi_outcome?: boolean;

  outcomes?: RequestOutcome[];

  // regarding user message in the request outcomes
  last_customer_message_at: string | null,
  last_message_at: string | null,
  last_message_by: LastMessageBy | null,

}

interface OrderResolutionsObject {
  main_size?: string;
  resize?: string[];
}

type RequestsCount = {
  total: string[];
  new: string[];
  unread_messages: string[];
};

// Note: 'uploading' may not be returned by the backend counts endpoint yet.
// All existing code accesses specific keys (queued, currently_editing, etc.) and
// iterates with Object.keys(), so a missing 'uploading' key is safe at runtime.
type RequestsCountsResponse = {
  [key in RequestStatus]: RequestsCount;
};

interface RequestAttentionRequest {
  attention_text: string;
}

export type {
  LastMessageBy,
  OrderResolutionsObject,
  RequestAttentionRequest,
  RequestComplexityType,
  RequestPriorityType,
  RequestsCount,
  RequestsCountsResponse,
  RequestsTableResponse,
  RequestStatus,
};
export {
  lastMessageBy,
  requestComplexity,
  requestComplexityLabels,
  requestPriority,
  requestPriorityLabels,
  requestStatus,
};
